/* Name-prompt dialog — a small text-input prompt (rename, duplicate-as).
 * Pre-fills an editable name and confirms it. Built on the shared shadcn
 * Dialog, mirroring ConfirmDialog's shape so both read the same. */
import React, {
    useEffect,
    useRef,
    useState
} from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from 'shared/components/ui/dialog';

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description?: React.ReactNode;
    /** Field label above the input. */
    label: string;
    /** Pre-filled, editable starting value (re-seeded each time it opens). */
    initialValue: string;
    confirmLabel: string;
    /** Leading title icon (lucide node), to match the other dialogs. */
    icon?: React.ReactNode;
    placeholder?: string;
    busy?: boolean;
    onConfirm: (value: string) => void;
};

export default function NamePromptDialog({
    open,
    onOpenChange,
    title,
    description,
    label,
    initialValue,
    confirmLabel,
    icon,
    placeholder,
    busy = false,
    onConfirm
}: Props) {
    const [value, setValue] = useState(initialValue);
    const inputRef = useRef<HTMLInputElement>(null);

    // Re-seed the field every time the dialog opens (or the source name
    // changes) so it always reflects the CURRENT target, then select-all so
    // the pre-filled text is immediately editable.
    useEffect(() => {
        if (open) {
            setValue(initialValue);
            const t = setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 30);
            return () => clearTimeout(t);
        }
    }, [open, initialValue]);

    const trimmed = value.trim();
    const canConfirm = !!trimmed && !busy;

    const confirm = () => {
        if (!canConfirm) return;
        onConfirm(trimmed);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[440px]">
                <DialogHeader>
                    <DialogTitle style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                    }}>
                        {icon}
                        {title}
                    </DialogTitle>
                    {description && (
                        <DialogDescription asChild>
                            <div>{description}</div>
                        </DialogDescription>
                    )}
                </DialogHeader>
                <div>
                    <label className="aps-label">{label}</label>
                    <input
                        ref={inputRef}
                        className="aps-input"
                        value={value}
                        placeholder={placeholder}
                        disabled={busy}
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                confirm();
                            }
                        }}
                    />
                </div>
                <DialogFooter>
                    <button className="aps-btn aps-btn-ghost" disabled={busy}
                        onClick={() => onOpenChange(false)}>
                        Cancel
                    </button>
                    <button className="aps-btn aps-btn-primary"
                        disabled={!canConfirm}
                        onClick={confirm}>
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
